/**
 * The rules, on your own screen, during the game.
 *
 * **Personal, not the table.** The table is identical for every viewer and
 * holds nothing to click - it may well be a television nobody can reach. One
 * person wanting to check whether an eight can be played on anything must not
 * take the shared view away from everybody else, and on a phone the table is a
 * backdrop behind the hand that cannot be scrolled. Reading is done at your own
 * pace, so it belongs on the surface that is already yours.
 *
 * The text is the same `howToPlay` the game's detail page shows, sent with the
 * join. One source, so the rules you read before playing are the rules you read
 * during it.
 */

import { useEffect, useRef } from 'react';

export function Rules({
  gameName,
  objective,
  howToPlay,
  onClose,
}: {
  gameName: string;
  objective: string;
  howToPlay: readonly string[];
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement | null>(null);

  /*
   * Focus moves into the sheet, and Escape closes it.
   *
   * A dialog that opens without taking focus leaves a keyboard or screen
   * reader user still on the hand behind it, tabbing through cards they cannot
   * see.
   */
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-label={`How to play ${gameName}`}>
      <div className="sheet-body rules-sheet">
        <p className="sheet-title">How to play {gameName}</p>

        {objective ? <p className="rules-objective">{objective}</p> : null}

        {howToPlay.length > 0 ? (
          <ul className="rules-list">
            {howToPlay.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        ) : (
          // A game whose catalogue entry has no rules text is a gap in the
          // config, not a reason to show an empty box with no explanation.
          <p className="field-help">No rules were written down for this one yet.</p>
        )}

        <button ref={closeRef} type="button" className="big-button" onClick={onClose}>
          Back to the game
        </button>
      </div>
    </div>
  );
}
